// <countdown-timer> (snippets/countdown-timer.liquid). Re-runs on theme editor section reloads via connected/disconnectedCallback.
(() => {
  if (customElements.get('countdown-timer')) return; // snippet loads this once per timer on the page

  const UNIT_MS = { days: 864e5, hours: 36e5, minutes: 6e4, seconds: 1e3 };

  // "2026-10-31 23:59" (time optional) + "+05:30" -> epoch ms, or null if the format is wrong
  const parse = (s, tz) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{1,2}):(\d{2}))?$/.exec((s || '').trim());
    if (!m) return null;
    const t = Date.parse(`${m[1]}-${m[2]}-${m[3]}T${(m[4] || '0').padStart(2, '0')}:${m[5] || '00'}:00${tz}`);
    return Number.isNaN(t) ? null : t;
  };

  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      const d = this.dataset;
      this.units = Array.from(this.querySelectorAll('[data-unit]'), (el) => ({
        el, unit: el.dataset.unit, tile: el.querySelector('.cd__tile'), num: el.querySelector('.cd__num'), value: null,
      }));
      this.flip = 'flip' in d && !matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.cycle = Number(d.duration) * 6e4;

      if ('previewEnd' in d) return this.finish();
      if (d.mode === 'evergreen') {
        if (!this.cycle) return this.hide('Countdown: set a rolling duration above 0.');
        this.end = this.rollingEnd();
      } else {
        this.end = parse(d.end, d.tz);
        if (!this.end) return this.hide('Countdown: end date must look like 2026-10-31 23:59');
        if (d.start) {
          this.start = parse(d.start, d.tz);
          if (!this.start) return this.hide('Countdown: "Show from" must look like 2026-10-25 09:00');
        }
      }
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() { clearInterval(this.timer); }

    // Each visitor's countdown starts on their first view; key includes the duration so editing it starts fresh.
    rollingEnd() {
      const key = `countdown:${this.dataset.key}:${this.cycle}`;
      const now = Date.now();
      let first;
      try { first = Number(localStorage.getItem(key)); } catch (e) {}
      if (!first || first > now) {
        first = now;
        try { localStorage.setItem(key, String(first)); } catch (e) {}
      }
      if ('repeat' in this.dataset) return now + this.cycle - ((now - first) % this.cycle);
      return first + this.cycle;
    }

    tick() {
      const now = Date.now();
      if (this.start && now < this.start) return this.hide(`Countdown: hidden until ${this.dataset.start} (${this.dataset.tz}).`);
      let left = this.end - now;
      if (left <= 0 && this.dataset.mode === 'evergreen' && 'repeat' in this.dataset) {
        this.end += this.cycle;
        left += this.cycle;
      }
      if (left <= 0) return this.finish();

      this.dataset.state = 'live';
      // Largest visible unit absorbs the rest, e.g. Days off -> "72" hours
      let rest = left;
      this.units.forEach((u) => {
        const v = Math.floor(rest / UNIT_MS[u.unit]);
        rest -= v * UNIT_MS[u.unit];
        u.el.hidden = u.unit === 'days' && v === 0 && 'hideZeroDays' in this.dataset;
        this.set(u, String(v).padStart(2, '0'));
      });
    }

    set(u, text) {
      if (u.value === text) return;
      const old = u.value;
      u.value = text;
      u.num.textContent = text;
      if (!this.flip || old === null) return;

      // Number underneath is already new: old top flap falls away, new bottom flap drops onto the old bottom.
      const half = (cls, t) => {
        const h = document.createElement('span');
        h.className = `cd__half ${cls}`;
        h.setAttribute('aria-hidden', 'true');
        h.appendChild(document.createElement('span')).textContent = t;
        return h;
      };
      const clear = () => u.tile.querySelectorAll('.cd__half').forEach((h) => h.remove());
      clear();
      u.tile.append(half('cd__half--bot', old), half('cd__half--top cd__flap-out', old), half('cd__half--bot cd__flap-in', text));
      clearTimeout(u.flipTimer);
      u.flipTimer = setTimeout(clear, 650);
    }

    finish() {
      clearInterval(this.timer);
      this.units.forEach((u) => this.set(u, '00'));
      if (this.querySelector('.cd__ended')) this.dataset.state = 'ended';
      else this.hide('Countdown has ended, so it is hidden on the live store. Set a "when timer ends" message or image to show one instead.');
    }

    // Hidden on the live store; in the theme editor CSS shows the note instead so the block can still be selected.
    hide(note) {
      this.dataset.state = 'hidden';
      const el = this.querySelector('.cd__note');
      if (el) el.textContent = note;
    }
  }

  customElements.define('countdown-timer', CountdownTimer);
})();
